"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPastMonthPeriods = exports.calculateValuesForPeriods = exports.getReportableTxn = exports.monthlyFrequencyData = exports.spendingFilters = exports.watchlistFilters = exports.incomeFilters = exports.isTxnItemReportableIncome = exports.isTxnItemReportableSpending = exports.isPostedOnDateInterval = void 0;
const immutable_1 = require("immutable");
const luxon_1 = require("luxon");
const transactions_1 = require("companion-app-components/flux/transactions");
const utils_1 = require("components/QCurrency/utils");
const utils_2 = require("data/transactions/utils");
const types_1 = require("./types");
// ================================================================================================
// Txn Filters
// ================================================================================================
// Filter to determine whether a txn is a candidate for reportable income. To be a candidate, txn must fall within the
// date interval and either have a regular CATEGORY or be a split-transaction.
//
const isPostedOnDateInterval = (txn, dateInterval) => {
    const postedOn = luxon_1.DateTime.fromISO(txn.postedOn);
    return !(!txn || !dateInterval.contains(postedOn));
};
exports.isPostedOnDateInterval = isPostedOnDateInterval;
function isTxnCandidateForReportableIncome(txn, dateInterval) {
    const postedOn = luxon_1.DateTime.fromISO(txn.postedOn);
    if (!dateInterval.contains(postedOn)) {
        return false;
    }
    return ((txn.coa && txn.coa.type === 'CATEGORY') || transactions_1.transactionsUtils.isSplitTxn(txn));
}
// Filter to determine whether a txn is a candidate for reportable expense. To be a candidate, txn must fall within the
// date interval and either have a regular CATEGORY, be UNCATEGORIZED or be a split-transaction.
function isTxnCandidateForReportableSpending(txn, dateInterval) {
    const postedOn = luxon_1.DateTime.fromISO(txn.postedOn);
    if (!dateInterval.contains(postedOn)) {
        return false;
    }
    return ((txn.coa && (txn.coa.type === 'CATEGORY' || txn.coa.type === 'UNCATEGORIZED')) || transactions_1.transactionsUtils.isSplitTxn(txn));
}
const isTxnItemReportableSpending = (item, categoriesById) => {
    if (!item.coa) {
        return false;
    }
    if (item.coa.type && item.coa.type === 'UNCATEGORIZED') {
        return true;
    }
    const category = categoriesById.get(item.coa && item.coa.id);
    return (category && category.type === 'EXPENSE') || false;
};
exports.isTxnItemReportableSpending = isTxnItemReportableSpending;
const isTxnItemReportableIncome = (item, categoriesById) => {
    if (!item.coa) {
        return false;
    }
    const category = categoriesById.get(item.coa.id);
    return (category && category.type === 'INCOME') || false;
};
exports.isTxnItemReportableIncome = isTxnItemReportableIncome;
exports.incomeFilters = {
    isTxnCandidateForReporting: isTxnCandidateForReportableIncome,
    isTxnItemReportable: exports.isTxnItemReportableIncome,
};
function isTxnItemReportableWatchlist(item, categoriesById) {
    var _a;
    if (!item.coa) {
        return false;
    }
    return (0, exports.isTxnItemReportableIncome)(item, categoriesById) || (0, exports.isTxnItemReportableSpending)(item, categoriesById) ||
        ((_a = item === null || item === void 0 ? void 0 : item.coa) === null || _a === void 0 ? void 0 : _a.type) === 'BALANCE_ADJUSTMENT';
}
exports.watchlistFilters = {
    isTxnCandidateForReporting: (txn, dateInterval) => isTxnCandidateForReportableIncome(txn, dateInterval) || isTxnCandidateForReportableSpending(txn, dateInterval),
    isTxnItemReportable: isTxnItemReportableWatchlist,
};
function payeesMatch(payee1, payee2) {
    if (payee1 && payee2) {
        const normalized1 = payee1 === null || payee1 === void 0 ? void 0 : payee1.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
        const normalized2 = payee2 === null || payee2 === void 0 ? void 0 : payee2.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
        return normalized1 === normalized2;
    }
    return false;
}
function isTxnHasCategory(txn, category) {
    return txn.coa && txn.coa.id === category;
}
function isTxnHasTag(txn, tag) {
    if (txn.tags) {
        return !txn.tags.every((theTag) => theTag.id !== tag.id);
    }
    return false;
}
// parameter "txn" might not be a real txn, it might be a split item (which has no payee field), so caller has to pass in the txnPayee
function isTxnExcludedByWatchlistFilter(txn, filterCriteria, txnPayee = null) {
    let exclude = false;
    if (filterCriteria) {
        switch (filterCriteria.type) {
            case 'payees':
                exclude = filterCriteria.items.every((payee) => !payeesMatch(txn.payee || txnPayee, payee));
                break;
            case 'categories':
                exclude = filterCriteria.items.every((category) => !isTxnHasCategory(txn, category));
                break;
            case 'tags':
                exclude = filterCriteria.items.every((tag) => !isTxnHasTag(txn, tag));
                break;
            default: break;
        }
    }
    return exclude;
}
function isTxnExcludedByFilterCriteriaTopLevelCategoryConstraint(txn, filterCriteria) {
    let exclude = false;
    if (filterCriteria && filterCriteria.type === 'categories' && filterCriteria.topLevelOnly) {
        exclude = !isTxnHasCategory(txn, filterCriteria.parentCoaNodeId);
    }
    return exclude;
}
function isTxnExcludedBySource(txn, filterBySource) {
    let exclude = false;
    if (filterBySource) {
        // console.log(`(AAA) ${txn.payee}: ${txn.source} === ${filterBySource} | ${txn.source === filterBySource}`);
        exclude = (txn.source === filterBySource);
    }
    return exclude;
}
exports.spendingFilters = {
    isTxnCandidateForReporting: isTxnCandidateForReportableSpending,
    isTxnItemReportable: exports.isTxnItemReportableSpending,
};
// ================================================================================================
// Frequency Data
// ================================================================================================
exports.monthlyFrequencyData = {
    frequency: 'month',
    duration: 'months',
    keyFromPeriod: (period) => period.toFormat('yyyy.MM'),
    shortLabelFromPeriod: (period) => period.toFormat('MMM'),
    labelFromPeriod: (period) => period.toFormat('MMMM'),
    yearFromPeriod: (period) => period.toFormat('yyyy'),
};
// ================================================================================================
// Calculators
// ================================================================================================
const isTxnReportable = (txn, filter, filterCriteria, filterBySource, categoriesById, payee = null) => {
    const test1 = filter.isTxnItemReportable(txn, categoriesById);
    const test2 = !isTxnExcludedByWatchlistFilter(txn, filterCriteria, payee);
    const test3 = !isTxnExcludedByFilterCriteriaTopLevelCategoryConstraint(txn, filterCriteria);
    const test4 = !isTxnExcludedBySource(txn, filterBySource);
    return test1 && test2 && test3 && test4;
};
const isTxnNotExcluded = (txn) => !txn.isExcludedFromReports;
const getReportableTxn = (txn, categoriesById, filterCriteria, filter, filterBySource, isReportableTxn = isTxnReportable, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
isTxnNotExcludedFromReports = isTxnNotExcluded) => {
    var _a, _b;
    let amount = 0;
    let isReportable = false;
    if (!transactions_1.transactionsUtils.isSplitTxn(txn)) {
        if (isReportableTxn(txn, filter, filterCriteria, filterBySource, categoriesById, txn.payee)) {
            // We don't want to add scheduled transaction pending to the totals
            const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
            const isUnAcceptedTxnTypeTxn = (0, utils_2.isUnAcceptedTxnType)(txn);
            const txnDiff = txnDate === null || txnDate === void 0 ? void 0 : txnDate.diff(luxon_1.DateTime.local(), 'days').toObject().days;
            if (!isUnAcceptedTxnTypeTxn && !(txnDiff && txnDiff >= 0)) {
                amount = Number(txn.amount);
            }
            isReportable = Boolean(!isUnAcceptedTxnTypeTxn);
        }
    }
    else {
        const isUnAcceptedTxnTypeTxn = (0, utils_2.isUnAcceptedTxnType)(txn);
        if (!isUnAcceptedTxnTypeTxn) {
            let splitHadReportable = false;
            const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
            (_b = (_a = txn.split) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.forEach((item) => {
                if (isReportableTxn(item, filter, filterCriteria, filterBySource, categoriesById, txn.payee)) {
                    const splitDiff = txnDate === null || txnDate === void 0 ? void 0 : txnDate.diff(luxon_1.DateTime.local(), 'days').toObject().days;
                    if (!(splitDiff && splitDiff >= 0)) {
                        amount += Number(item.amount);
                    }
                    splitHadReportable = true;
                }
            });
            if (splitHadReportable) {
                isReportable = true;
            }
        }
    }
    return { amount, isReportable };
};
exports.getReportableTxn = getReportableTxn;
const calculateValuesForPeriods = (accountIds, accountsByIds, categoriesById, transactionsByAccountId, dateInterval, frequencyData, filter, filterCriteria, filterBySource) => {
    var _a;
    const periods = new Map();
    accountIds.forEach((accountId) => {
        const account = accountsByIds.get(accountId);
        const currency = account ? (0, utils_1.getCurrencyForAccount)(account) : '';
        if (!['BANK', 'CREDIT', 'CASH'].includes(account.type))
            return; // loan and asset account txns are to be filtered out
        const txns = transactionsByAccountId.get(accountId);
        (txns || []).forEach((txn) => {
            const period = luxon_1.DateTime.fromISO(txn.postedOn).startOf(frequencyData.frequency);
            const key = frequencyData.keyFromPeriod(period);
            const isC2RTxn = (0, utils_2.isC2RTransaction)(txn);
            let data = periods.get(key);
            if (!data) {
                periods.set(key, data = {
                    period,
                    amount: 0,
                    txns: [],
                    currency,
                });
            }
            const { amount, isReportable } = (0, exports.getReportableTxn)(txn, categoriesById, filterCriteria, filter, filterBySource);
            if (!isC2RTxn) {
                data.amount += amount;
            }
            if (isReportable && !isC2RTxn) {
                data.txns.push(txn);
            }
        });
    });
    const byPeriods = [];
    let txns = [];
    let currency = '';
    // Create byPeriods array in date-order (start->end) and all txns array. Will add values for periods with no income,
    // to insure we have keys for every period in date interval.
    //
    for (let period = (_a = dateInterval.start) === null || _a === void 0 ? void 0 : _a.startOf(frequencyData.frequency); dateInterval.contains(period); period = period.plus({ [frequencyData.duration]: 1 })) {
        const key = frequencyData.keyFromPeriod(period);
        const dataForPeriod = periods.get(key);
        const byPeriod = {
            period,
            label: frequencyData.labelFromPeriod(period),
            shortLabel: frequencyData.shortLabelFromPeriod(period),
            year: frequencyData.yearFromPeriod(period),
            amount: dataForPeriod ? dataForPeriod.amount : 0,
            currency: dataForPeriod ? dataForPeriod.currency : '',
            txns: (0, immutable_1.List)(dataForPeriod ? dataForPeriod.txns : []),
        };
        byPeriods.push(byPeriod);
        txns = txns.concat(dataForPeriod ? dataForPeriod.txns : []);
        currency = (0, utils_1.getCombinedCurrency)(currency, dataForPeriod ? dataForPeriod.currency : '');
    }
    return new types_1.OTReturn({
        periods: (0, immutable_1.List)(byPeriods),
        txns: (0, immutable_1.List)(txns),
        currency,
    });
};
exports.calculateValuesForPeriods = calculateValuesForPeriods;
// NOTE: if an injectedKey is an object each period will have the same object in it
function createPastMonthPeriods(injectedKeys, numMonths) {
    let periods = (0, immutable_1.List)();
    for (let i = 0; i < numMonths; i++) {
        const month = luxon_1.DateTime.local().endOf('month').minus({ months: i });
        periods = periods.set(i, Object.assign({ monthLong: month.monthLong, monthShort: month.monthShort, year: month.year, date: month.toISO(), txns: (0, immutable_1.List)(), mutableTxns: [] }, injectedKeys));
    }
    return periods.reverse();
}
exports.createPastMonthPeriods = createPastMonthPeriods;
